import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowLeft,
  ArrowSquareOut,
  Eye,
  QrCode,
  SealCheck,
  FilmStrip,
  Images,
  Play,
  Prohibit,
  UsersFour,
  VideoCamera,
  Waveform,
} from "@phosphor-icons/react";
import { Link as RouterLink } from "react-router-dom";
import { Mono, SectionLabel, StatusChip, VideoFrame } from "../ui";
import { caps, fonts } from "../theme";
import {
  blobUrl,
  http,
  DicomNode,
  Instance,
  OrderDetail as Detail,
  OrderSession,
  Recording,
  SessionParticipantRecord,
  StreamInfo,
} from "../api";
import { useSession } from "../AuthToken";
import { useLive } from "../live";
import { WhepPlayer } from "../WhepPlayer";
import { RecordingPlayer } from "../RecordingPlayer";

const mb = (n: number) => `${(n / 1048576).toFixed(1)} MB`;
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtDuration = (ms: number) => {
  const m = Math.round(ms / 60000);
  return m < 1 ? "Under 1 min" : m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};
const EYE: Record<string, string> = { R: "OD (right)", L: "OS (left)", B: "OU (both)" };

export default function OrderDetail() {
  const { id } = useParams();
  const { token, me } = useSession();
  const [o, setO] = useState<Detail | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [stream, setStream] = useState<StreamInfo | null>(null);
  const [playing, setPlaying] = useState<{ sessionId: string; recId: string } | null>(null);
  const [nodes, setNodes] = useState<DicomNode[] | null>(null);
  useEffect(() => {
    if (token) http.get<DicomNode[]>(token, "/nodes").then((r) => setNodes(r.ok ? r.result : []));
  }, [token]);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const r = await http.get<Detail>(token, `/orders/${id}`);
    if (!r.ok) return setError(r.error);
    setO(r.result);
    for (const i of r.result.instances) {
      if ((i.previewable ?? i.storage === "healthimaging") && !thumbs[i.id]) {
        const url = await blobUrl(token, `/instances/${i.id}/rendered`);
        if (url) setThumbs((t) => ({ ...t, [i.id]: url }));
      }
    }
  };
  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);
  // re-read when this order (or anything captured for it) changes, pushed by Jupiter rather than polled
  useLive(token, (e) => {
    if (e.kind === "connect" || e.orderId === id || e.orderId === null) load();
  });

  // what the order's instrument can do: live video (and so remote sessions) only on instruments that provide it
  const stationNode = nodes?.find((x) => x.aet === o?.stationAeTitle);
  const remoteCapable = !!stationNode?.live;
  const watch = async () => {
    if (o?.stationAeTitle === "*")
      return setError(
        "This order is loaded to all instruments. Select a single instrument under Load to, then try again."
      );
    const n = stationNode;
    if (!n?.live) return setError(`Instrument ${o?.stationAeTitle} does not provide live video.`);
    const s = await http.get<StreamInfo>(token, `/nodes/${n.id}/stream`);
    if (s.ok) setStream(s.result);
    else setError(s.error);
  };
  // Tech at the lamp: start a remote session for this order on its instrument; the session page shows the QR.
  const startSession = async () => {
    if (o?.stationAeTitle === "*")
      return setError(
        "This order is loaded to all instruments. Select a single instrument under Load to, then try again."
      );
    const n = stationNode;
    if (!n?.live)
      return setError(
        `Instrument ${o?.stationAeTitle} does not provide live video, so it cannot be used for a remote session.`
      );
    const r = await http.post<{ id: string; pass: { url: string; expiresAt: string } }>(token, "/sessions", {
      orderId: id,
      nodeId: n.id,
    });
    if (!r.ok) {
      // Already live for this order: open that session instead of starting a second one.
      const live = r.error.startsWith("409")
        ? await http.get<{ id: string; accession: string }[]>(token, "/sessions?status=live")
        : null;
      const existing = live?.ok ? live.result.find((x) => x.accession === o?.accession) : undefined;
      return existing ? navigate(`/sessions/${existing.id}`) : setError(r.error);
    }
    try {
      sessionStorage.setItem(`pass:${r.result.id}`, JSON.stringify(r.result.pass));
    } catch {
      /* the session page can issue a fresh pass */
    }
    navigate(`/sessions/${r.result.id}`);
  };
  const play = async (recId: string) => {
    const r = await http.get<{ url: string }>(token, `/recordings/${recId}/url`);
    if (r.ok) window.open(r.result.url, "_blank", "noopener");
  };
  const cancel = async () => {
    const r = await http.post(token, `/admin/orders/${id}/cancel`);
    if (!r.ok) setError(r.error);
    load();
  };

  if (!o) return error ? <Alert severity="error">{error}</Alert> : null;
  const canAct = me?.role === "admin" || me?.role === "tech";
  const canAssignEye = me?.role === "admin" || me?.role === "tech" || me?.role === "reader";
  const viewers = [
    ...new Map((o?.instances ?? []).filter((i) => i.viewer).map((i) => [i.viewer!.url, i.viewer!])).values(),
  ];
  const exports = o.recordings.filter((r) => r.source === "export");
  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="warning" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Box>
        <Button
          component={RouterLink}
          to="/worklist"
          size="small"
          color="inherit"
          startIcon={<ArrowLeft size={14} />}
          sx={{ color: "text.secondary", ml: -1.5, mb: 1 }}
        >
          Worklist
        </Button>
        <Paper sx={{ p: { xs: 2, md: 3 }, position: "relative", overflow: "hidden" }}>
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              right: -120,
              top: -160,
              width: 360,
              height: 360,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(217,162,95,0.12), transparent 65%)",
              pointerEvents: "none",
            }}
          />
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ md: "center" }}
            sx={{ position: "relative" }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="overline" sx={{ color: "primary.main", lineHeight: 1.6 }}>
                  Order
                </Typography>
                <StatusChip status={o.status} />
              </Stack>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: 26, md: 32 } }}>
                {o.patient.familyName}, {o.patient.givenName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                MRN <Mono sx={{ color: "text.primary" }}>{o.patient.mrn}</Mono> ({o.patient.issuer}){" "}
                {o.patient.birthDate ? `· DOB ${o.patient.birthDate}` : ""} {o.patient.sex ? `· ${o.patient.sex}` : ""}
              </Typography>
            </Box>
            <Box sx={{ borderLeft: { md: 1 }, borderColor: { md: "divider" }, pl: { md: 3 } }}>
              <Typography sx={{ ...caps, fontSize: 10, color: "text.secondary" }}>Accession</Typography>
              <Mono sx={{ fontSize: 15, color: "primary.light" }}>{o.accession}</Mono>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {o.procedureDescription}
              </Typography>
              {me?.role === "admin" && o.status !== "cancelled" ? (
                <LoadTo order={o} onSaved={load} onError={setError} />
              ) : (
                <Typography variant="body2">
                  Loaded to{" "}
                  {o.stationAeTitle === "*" ? "all instruments at this site" : <Mono>{o.stationAeTitle}</Mono>}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {new Date(o.scheduledAt).toLocaleString()}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2.5, position: "relative" }}>
            {remoteCapable && canAct && ["scheduled", "in_progress"].includes(o.status) && (
              <Button variant="contained" startIcon={<VideoCamera size={16} weight="regular" />} onClick={startSession}>
                Start session
              </Button>
            )}
            {remoteCapable && (
              <Button variant="outlined" startIcon={<Eye size={16} />} onClick={watch}>
                Live view
              </Button>
            )}
            {nodes && !remoteCapable && o.stationAeTitle !== "*" && (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                {stationNode
                  ? `${
                      stationNode.displayName ?? stationNode.aet
                    } provides worklist and storage. It does not provide live video, so remote sessions are not available for this order.`
                  : `Instrument ${o.stationAeTitle} is not approved in Jupiter.`}
              </Typography>
            )}
            {o.stationAeTitle === "*" && (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                To start a remote session, load this order to a single instrument.
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {canAct && o.status !== "cancelled" && (
              <Button color="error" startIcon={<Prohibit size={16} />} onClick={cancel}>
                Cancel order
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>
      {stream && (
        <Paper sx={{ p: 2 }}>
          <WhepPlayer stream={stream} token={token} />
        </Paper>
      )}
      {o.sessions.length > 0 && (
        <Stack spacing={1.5}>
          <SectionLabel icon={<Waveform size={16} />} count={o.sessions.length}>
            Sessions
          </SectionLabel>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
            }}
          >
            {o.sessions.map((sess) => (
              <Box key={sess.id}>
                <SessionCard
                  sess={sess}
                  rec={o.recordings.find((r) => r.sessionId === sess.id && r.source === "session")}
                  token={token}
                  onPlay={(recId) => setPlaying({ sessionId: sess.id, recId })}
                  onDetails={() => navigate(`/sessions/${sess.id}`)}
                />
              </Box>
            ))}
          </Box>
          <Dialog open={!!playing} onClose={() => setPlaying(null)} maxWidth="lg" fullWidth>
            {playing && (
              <>
                <DialogTitle>Session recording</DialogTitle>
                <DialogContent>
                  <RecordingPlayer recordingId={playing.recId} token={token} />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => navigate(`/sessions/${playing.sessionId}`)}
                    endIcon={<ArrowSquareOut size={14} />}
                  >
                    Session details
                  </Button>
                  <Button onClick={() => setPlaying(null)}>Close</Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        </Stack>
      )}
      <Stack spacing={1.5}>
        <SectionLabel icon={<Images size={16} />} count={o.instances.length}>
          Captured objects
        </SectionLabel>
        {viewers.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {viewers.map((v) => (
              <Button
                key={v.url}
                variant="outlined"
                size="small"
                endIcon={<ArrowSquareOut size={14} />}
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in {v.name}
              </Button>
            ))}
          </Stack>
        )}
        {o.instances.length > 0 && (
          <Alert severity="info" variant="outlined">
            Images in Jupiter are for reference only and are not for diagnostic use. Review studies for diagnosis in the
            instrument&apos;s review software
            {viewers.length ? "." : ". An administrator can add a link to it in the instrument's module settings."}
          </Alert>
        )}
        {o.instances.some((i) => thumbs[i.id]) && (
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
            {o.instances
              .filter((i) => thumbs[i.id])
              .map((i) => (
                <Box key={i.id}>
                  <VideoFrame glow="warm">
                    <img src={thumbs[i.id]} alt={i.sopClassName} style={{ width: "100%", display: "block" }} />
                  </VideoFrame>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                    {i.laterality ? `${EYE[i.laterality]} · ` : "Eye not recorded · "}
                    {i.sopClassName} · {new Date(i.receivedAt).toLocaleString()} · Reference only
                  </Typography>
                </Box>
              ))}
          </Box>
        )}
        <Paper sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Received</TableCell>
                <TableCell>Object</TableCell>
                <TableCell>Eye</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Storage</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Validation</TableCell>
                <TableCell>Review</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {o.instances.map((i) => (
                <TableRow key={i.id}>
                  <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                    {new Date(i.receivedAt).toLocaleString()}
                  </TableCell>
                  <TableCell>{i.sopClassName}</TableCell>
                  <TableCell>
                    <EyeCell inst={i} canEdit={canAssignEye} onSaved={load} onError={setError} />
                  </TableCell>
                  <TableCell>
                    <Mono>{i.fromAet}</Mono>
                  </TableCell>
                  <TableCell>{i.storage === "healthimaging" ? "HealthImaging" : "S3 (exact bytes)"}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{mb(i.sizeBytes)}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        [...i.validation.missing.map((m) => `missing ${m}`), ...i.validation.warnings].join("; ") ||
                        i.validation.profile
                      }
                    >
                      <span>
                        <StatusChip
                          status={i.validation.ok ? "ok" : "failed"}
                          tone={i.validation.ok ? (i.validation.warnings.length ? "warn" : "ok") : "fail"}
                          label={
                            i.validation.ok ? `${i.validation.profile} ok` : `${i.validation.missing.length} missing`
                          }
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {i.viewer ? (
                      <Button
                        size="small"
                        endIcon={<ArrowSquareOut size={14} />}
                        href={i.viewer.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No viewer link
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {o.instances.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                    Nothing captured yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Stack>
      <Stack spacing={1.5}>
        <SectionLabel icon={<FilmStrip size={16} />} count={exports.length}>
          Instrument exports
        </SectionLabel>
        <Paper sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Size</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {exports.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ color: "text.secondary" }}>{new Date(r.startedAt).toLocaleString()}</TableCell>
                  <TableCell>Instrument export</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{mb(r.sizeBytes)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<Play size={14} />} onClick={() => play(r.id)}>
                      Play
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {exports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                    No exports from the instrument.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Stack>
    </Stack>
  );
}

type LoadNode = DicomNode & { worklistEnabled?: boolean; network?: { worklistEnabled?: boolean } };
const ALL = "*";

/**
 * Which instrument's worklist this order appears on. "*" puts it on every worklist-enabled instrument at the site
 * (Jupiter writes one worklist entry per instrument, since Orthanc matches the station AE exactly).
 */
// A participant as recorded: the account (or, for a QR-joined device, the staff member who admitted it), with role,
// where they took part and when they joined and left.
function ParticipantChip({ p }: { p: SessionParticipantRecord }) {
  const id = p.identity;
  const t = (iso: string | null) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
  const where = p.kind === "remote" ? "Remote" : "In the exam room";
  const lines = [
    id?.via === "account"
      ? `Signed in: ${id.email ?? id.name}${id.role ? ` (${id.role})` : ""}`
      : id?.via === "pass"
      ? `Joined with a QR code issued by ${id.issuedBy?.email ?? id.issuedBy?.name ?? "unknown"}`
      : "Identity not recorded",
    where,
    `Joined ${t(p.joinedAt)}${p.removedAt ? `, removed ${t(p.removedAt)}` : p.leftAt ? `, left ${t(p.leftAt)}` : ""}`,
  ];
  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{lines.join("\n")}</span>}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ px: 1, py: 0.25, borderRadius: "999px", border: 1, borderColor: "divider", typography: "caption" }}
      >
        {id?.via === "account" ? <SealCheck size={13} /> : <QrCode size={13} />}
        <span>{p.label}</span>
      </Stack>
    </Tooltip>
  );
}

// One remote session as a card: a frame from its recording, when it ran, and who took part.
function SessionCard({
  sess,
  rec,
  token,
  onPlay,
  onDetails,
}: {
  sess: OrderSession;
  rec?: Recording;
  token: string;
  onPlay: (recId: string) => void;
  onDetails: () => void;
}) {
  const [poster, setPoster] = useState("");
  useEffect(() => {
    if (!rec) return;
    http.get<{ url: string }>(token, `/recordings/${rec.id}/url`).then((r) => r.ok && setPoster(`${r.result.url}#t=2`));
  }, [rec, token]);
  const ms = sess.endedAt ? new Date(sess.endedAt).getTime() - new Date(sess.startedAt).getTime() : null;
  const status =
    sess.status === "live"
      ? "live"
      : rec
      ? "ready"
      : sess.mergeStatus === "failed"
      ? "failed"
      : sess.mergeStatus === "empty"
      ? "nothing recorded"
      : "processing";
  const end = sess.endedAt
    ? new Date(sess.endedAt).toDateString() === new Date(sess.startedAt).toDateString()
      ? new Date(sess.endedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : fmtTime(sess.endedAt)
    : "in progress";
  return (
    <Paper sx={{ overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
      <ButtonBase
        disabled={!rec}
        onClick={() => rec && onPlay(rec.id)}
        sx={{ display: "block", width: "100%", textAlign: "left" }}
        aria-label="Play recording"
      >
        <VideoFrame
          aspect="16 / 9"
          glow={sess.status === "live" ? "live" : "none"}
          sx={{ borderRadius: 0, border: "none" }}
        >
          {poster ? (
            <video
              src={poster}
              preload="metadata"
              muted
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ position: "absolute", inset: 0, color: "text.secondary" }}
            >
              <Waveform size={28} />
              <Typography variant="caption" sx={{ mt: 1 }}>
                {status === "live" ? "Session in progress" : `Recording ${status}`}
              </Typography>
            </Stack>
          )}
          {rec && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.25)",
                opacity: 0.85,
                "&:hover": { opacity: 1, bgcolor: "rgba(0,0,0,0.4)" },
              }}
            >
              <Play size={36} weight="fill" color="#fff" />
            </Stack>
          )}
        </VideoFrame>
      </ButtonBase>
      <Stack spacing={1} sx={{ p: 2, flexGrow: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            {fmtTime(sess.startedAt)} to {end}
          </Typography>
          <StatusChip status={status} label={status} />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {ms !== null ? `${fmtDuration(ms)} · ` : ""}
          {sess.node.displayName ?? sess.node.aet}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {sess.participants.length ? (
            sess.participants.map((p) => <ParticipantChip key={p.id} p={p} />)
          ) : (
            <Typography variant="caption" color="text.secondary">
              No participants
            </Typography>
          )}
        </Stack>
        {status === "failed" && sess.mergeError && (
          <Typography variant="caption" color="error">
            {sess.mergeError}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          {rec && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Play size={14} weight="fill" />}
              onClick={() => onPlay(rec.id)}
            >
              Review recording
            </Button>
          )}
          <Button size="small" endIcon={<ArrowSquareOut size={14} />} onClick={onDetails}>
            {sess.status === "live" ? "Open session" : "Session details"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

// Eye for one object. The instrument's own value is final; when it recorded none, staff can assign one, which is
// stored as user-assigned with who and when, and sent to the EMR marked that way.
function EyeCell({
  inst,
  canEdit,
  onSaved,
  onError,
}: {
  inst: Instance;
  canEdit: boolean;
  onSaved: () => void;
  onError: (e: string) => void;
}) {
  const [saving, setSaving] = useState(false);
  const { token } = useSession();
  if (inst.lateralitySource === "device" && inst.laterality) {
    return (
      <Tooltip title="Recorded by the instrument">
        <span>
          <StatusChip status="ok" tone="ok" label={EYE[inst.laterality]} />
        </span>
      </Tooltip>
    );
  }
  const assigned = inst.lateralitySource === "user" && inst.laterality;
  const note =
    assigned && inst.lateralitySetAt
      ? `Assigned in Jupiter by ${inst.lateralitySetBy ?? "a user"} on ${new Date(
          inst.lateralitySetAt
        ).toLocaleString()}`
      : "The instrument did not record the eye";
  if (!canEdit)
    return (
      <Tooltip title={note}>
        <Typography variant="body2" color={assigned ? "text.primary" : "text.secondary"}>
          {assigned ? `${EYE[inst.laterality!]} (assigned)` : "Not recorded"}
        </Typography>
      </Tooltip>
    );
  const save = async (v: string) => {
    setSaving(true);
    const r = await http.patch(token, `/instances/${inst.id}/laterality`, { laterality: v || null });
    setSaving(false);
    if (!r.ok) return onError(r.error);
    onSaved();
  };
  return (
    <Tooltip title={note} placement="top">
      <TextField
        select
        size="small"
        variant="standard"
        disabled={saving}
        value={inst.laterality ?? ""}
        onChange={(e) => save(e.target.value)}
        SelectProps={{ displayEmpty: true }}
        sx={{ minWidth: 150 }}
        inputProps={{ "aria-label": "Eye" }}
      >
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">
            Not recorded
          </Typography>
        </MenuItem>
        <MenuItem value="R">OD (right), assigned</MenuItem>
        <MenuItem value="L">OS (left), assigned</MenuItem>
        <MenuItem value="B">OU (both), assigned</MenuItem>
      </TextField>
    </Tooltip>
  );
}

function LoadTo({ order, onSaved, onError }: { order: Detail; onSaved: () => void; onError: (e: string) => void }) {
  const { token } = useSession();
  const [nodes, setNodes] = useState<LoadNode[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!token) return;
    http.get<LoadNode[]>(token, "/nodes").then((r) => setNodes(r.ok ? r.result : []));
  }, [token]);

  // Prefer worklist-enabled instruments when the server says which they are; otherwise every approved one.
  const flag = (n: LoadNode) => n.worklistEnabled ?? n.network?.worklistEnabled;
  const known = (nodes ?? []).some((n) => typeof flag(n) === "boolean");
  const options = (nodes ?? []).filter((n) => !known || flag(n));
  const current = order.stationAeTitle;
  const orphan = current !== ALL && nodes !== null && !options.some((n) => n.aet === current);

  const change = async (stationAeTitle: string) => {
    if (stationAeTitle === current) return;
    setBusy(true);
    const r = await http.patch(token, `/admin/orders/${order.id}`, { stationAeTitle });
    setBusy(false);
    if (!r.ok)
      return onError(
        r.error.startsWith("404")
          ? "Changing the instrument for this order is not available on this server yet."
          : r.error
      );
    onSaved();
  };

  return (
    <TextField
      select
      size="small"
      label="Load to"
      value={nodes === null && current !== ALL ? "" : current}
      disabled={busy || nodes === null}
      onChange={(e) => change(e.target.value)}
      sx={{ mt: 1.25, minWidth: 240, maxWidth: "100%" }}
      SelectProps={{
        renderValue: (v) =>
          v === ALL ? (
            "All instruments at this site"
          ) : (
            <Box component="span" sx={{ fontFamily: fonts.mono }}>
              {String(v || current)}
            </Box>
          ),
      }}
      helperText={current === ALL ? "Every worklist-enabled instrument at this site shows this order" : undefined}
    >
      <MenuItem value={ALL}>
        <Stack direction="row" spacing={1} alignItems="center">
          <UsersFour size={16} />
          <span>All instruments at this site</span>
        </Stack>
      </MenuItem>
      {options.length > 0 && (
        <ListSubheader sx={{ bgcolor: "transparent", lineHeight: "32px" }}>Instruments</ListSubheader>
      )}
      {options.map((n) => (
        <MenuItem key={n.id} value={n.aet}>
          <Box>
            <Box sx={{ fontFamily: fonts.mono, fontSize: 13 }}>{n.aet}</Box>
            {(n.displayName || n.device?.siteCode) && (
              <Typography variant="caption" color="text.secondary">
                {[n.displayName, n.device?.siteCode].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Box>
        </MenuItem>
      ))}
      {orphan && (
        <MenuItem value={current} disabled>
          <Box>
            <Box sx={{ fontFamily: fonts.mono, fontSize: 13 }}>{current}</Box>
            <Typography variant="caption" color="text.secondary">
              Not an approved worklist instrument
            </Typography>
          </Box>
        </MenuItem>
      )}
    </TextField>
  );
}
